import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, generatePath, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons/faPaperPlane';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import toast from 'react-hot-toast';

import { bookingTypeConstant, bookingVerticalType } from 'us-react-components/dist/constants/Booking';
import { Avatar } from 'us-react-components/dist/Avatar/Avatar';
import { BetaBadge } from 'us-react-components/dist/BetaBadge/BetaBadge';
import { NuxContainer } from 'us-react-components/dist/NuxContainer/NuxContainer';
import { SpeechRecognition } from 'us-react-components/dist/SpeechRecognition/SpeechRecognition';

import {
  AiChatMessage,
  AiService,
  AiNuxRequest,
  JobAvailabilityType,
  ParentService,
  ParentSignupVerticalTypeService,
  ParentSignupVerticalTypeId,
  Parent,
} from 'us-web-services';
import { DateFormat, formatDateToString, getUserTimeZone, todayYmd } from 'us-react-components/dist/utils/date';
import { getUsAvatarSrc } from 'us-react-components/dist/utils/image';
import { Button, ButtonSize, ButtonType } from '../../../components/Button/Button';
import { useDependencies } from '../../../dependencies';
import useThinkingDots from '../../../hooks/useThinkingDots';
import { buildAiFamilyNuxFromStore, syncStoreFromAiNux } from '../../../models/FamilyRegistrationStore';
import { Routes } from '../../../Routes';
import { buildNuxFilterSearchParams, createSavedSearch } from '../../../utils';

export const Assistant = observer(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { stores: { chat, familyRegistration, parent }, services: { tracker } } = useDependencies();
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showAbortCta, setShowAbortCta] = useState(false);
  const thinkingDots = useThinkingDots(isLoading);

  const messages = chat.assistantMessages;
  const inputRef = useRef<HTMLInputElement>(null);
  const isNavigatingAwayRef = useRef<boolean>(false);

  familyRegistration.setCurrentRoute(Routes.parent.registration.assistant);

  useEffect(() => {
    if (!isLoading) {
      inputRef.current?.focus();
    }
  }, [isLoading]);

  useEffect(() => {
    if (chat.isHydrated && !isNavigatingAwayRef.current) {
      if (messages.length === 0) {
        chat.addMessage({
          id: Date.now(),
          role: 'assistant',
          content: t('pages.nux.family.assistant.welcome')
        });
      } else {
        handleScrollChatBottom();
      }
    }
  }, [chat.isHydrated]);


  const getCareTypeLabel = (): string => {
    const careType = bookingTypeConstant.find(type => type.id === familyRegistration.lookingForId);
    return careType?.label || 'Care';
  };

  const buildSitterSearchParams = (): Record<string, string> => {
    const lookingForId = familyRegistration.lookingForId ?? bookingVerticalType.CHILD_CARE;
    const address = familyRegistration.profile?.address;

    const searchTypeCode =
      familyRegistration.availabilityType === JobAvailabilityType.fullTime
        ? 'FULL_TIME'
        : familyRegistration.availabilityType === JobAvailabilityType.partTime
          ? 'PART_TIME'
          : familyRegistration.availabilityType === JobAvailabilityType.occasional
            ? 'OCCASIONAL'
            : 'ALL';

    const params: Record<string, string> = {
      verticalTypeId: lookingForId.toString(),
      searchTypeCode,
      page: '1',
      fromNux: 'true',
    };

    if (address?.city) params.city = address.city;
    if (address?.state) params.state = address.state;
    if (address?.zip) params.zip = address.zip;
    if (familyRegistration.countDependents) {
      params.numberOfChildren = familyRegistration.countDependents.toString();
    }

    switch (lookingForId) {
      case bookingVerticalType.SENIOR_CARE:
        params.willSitSenior = 'true';
        break;
      case bookingVerticalType.HOUSEHOLD_SERVICES:
        params.willSitHome = 'true';
        break;
      case bookingVerticalType.PET_CARE:
        if (familyRegistration.dog) params.willSitDog = 'true';
        if (familyRegistration.cat) params.willSitCat = 'true';
        if (familyRegistration.otherPet) params.willSitOtherPet = 'true';
        break;
      default:
        break;
    }

    return { ...params, ...buildNuxFilterSearchParams(familyRegistration) };
  };

  const getChildrenAgesString = (): string => {
    return familyRegistration.childrenAges
      .map(age => age.value)
      .filter(v => v)
      .join(', ');
  };

  const getLocationString = (): string => {
    const { address } = familyRegistration.profile || {};
    if (!address) return '';

    const parts = [address.city, address.state, address.zip].filter(Boolean);
    return parts.join(', ');
  };

  const getPetsString = (): string => {
    const pets = [];
    if (familyRegistration.dog) pets.push('Dog');
    if (familyRegistration.cat) pets.push('Cat');
    if (familyRegistration.otherPet) pets.push('Other pet');

    return pets.join(', ');
  };

  const getCareTimesString = (): string => {
    const start = familyRegistration.startTimeDisplay;
    const end = familyRegistration.endTimeDisplay;
    if (start && end) return `${start} – ${end}`;

    return start || end;
  };

  const getDatesString = (): string => {
    if (familyRegistration.availabilityType !== JobAvailabilityType.occasional || !familyRegistration.startDate) {
      return '';
    }
    const start = formatDateToString(familyRegistration.startDate, DateFormat.DATE_SHORT_WITH_YEAR);
    const end = familyRegistration.endDate
      ? formatDateToString(familyRegistration.endDate, DateFormat.DATE_SHORT_WITH_YEAR)
      : '';

    return end && end !== start ? `${start} – ${end}` : start;
  };

  const getLabelsString = (items: Array<{ label?: string }>): string =>
    items.map(item => item.label).filter(Boolean).join(', ');

  const getSignupVerticalTypeId = (): ParentSignupVerticalTypeId | undefined => {
    switch (familyRegistration.lookingForId) {
      case bookingVerticalType.CHILD_CARE:
        return ParentSignupVerticalTypeId.CHILD_CARE;
      case bookingVerticalType.PET_CARE:
        if (familyRegistration.dog) return ParentSignupVerticalTypeId.PET_CARE_DOG;
        if (familyRegistration.cat) return ParentSignupVerticalTypeId.PET_CARE_CAT;
        return ParentSignupVerticalTypeId.PET_CARE_OTHER;
      case bookingVerticalType.SENIOR_CARE:
        return ParentSignupVerticalTypeId.SENIOR_CARE;
      case bookingVerticalType.HOUSEHOLD_SERVICES:
        return ParentSignupVerticalTypeId.HOUSEHOLD_SERVICES;
      default:
        return undefined;
    }
  };

  const saveParentData = async (): Promise<void> => {
    try {
      if (familyRegistration.profile.id) {
        const signupVerticalTypeId = getSignupVerticalTypeId();
        let addedVertical;

        if (signupVerticalTypeId) {
          familyRegistration.setSignupVerticalTypeId(signupVerticalTypeId);

          const existingVerticalType = parent.parent?.signupVerticalTypes?.find(
            type => type.signupVerticalType?.id === signupVerticalTypeId
          );

          if (!existingVerticalType) {
            try {
              const verticalType = {
                parentId: familyRegistration.profile.id,
                signupVerticalType: {
                  id: signupVerticalTypeId,
                },
              };
              addedVertical = (await ParentSignupVerticalTypeService.create(verticalType)).data.data;
            } catch {
              toast.error(t('notifications.genericError'));
            }
          }
        }

        const params: Parent = {
          id: familyRegistration.profile.id,
          address: familyRegistration.profile.address,
          cat: familyRegistration.cat ?? undefined,
          dog: familyRegistration.dog ?? undefined,
          otherPet: familyRegistration.otherPet ?? undefined,
          signupVerticalTypes: [
            ...(parent.parent?.signupVerticalTypes || []),
            ...(addedVertical ? [addedVertical] : []),
          ],
        };

        const updatedParent = (await ParentService.patch(familyRegistration.profile.id, params)).data;
        parent.updateParent(updatedParent.data);
      }
    } catch {
      toast.error(t('notifications.genericError'));
    }
  };

  const saveSavedSearch = async (): Promise<void> => {
    if (!familyRegistration.availabilityType) {
      return;
    }

    try {
      await createSavedSearch(familyRegistration);
    } catch {
      toast.error(t('notifications.genericError'));
    }
  };

  const saveAllData = async (): Promise<void> => {
    await saveParentData();
    await saveSavedSearch();
  };

  const goToDashboard = async (): Promise<void> => {
    await saveAllData();
    isNavigatingAwayRef.current = true;

    navigate(Routes.parent.dashboard);
    chat.clearMessages();
  };

  const goToJobPost = async (): Promise<void> => {
    await saveAllData();
    isNavigatingAwayRef.current = true;

    navigate({
      pathname: generatePath(Routes.parent.registration.details),
    });
    chat.clearMessages();
  };

  const safeBuildSitterSearchParams = (): Record<string, string> => {
    try {
      return buildSitterSearchParams();
    } catch (error) {
      console.error('Failed to build sitter search params, navigating with no filters', error);

      return {};
    }
  };

  const goToSitterSearch = async (): Promise<void> => {
    await saveAllData();
    isNavigatingAwayRef.current = true;

    navigate({
      pathname: generatePath(Routes.parent.sitters),
      search: `${createSearchParams(safeBuildSitterSearchParams())}`,
    });
    chat.clearMessages();
  };

  const renderSummaryItem = (label: string, value: string | undefined): JSX.Element | null => {
    if (!value) return null;

    return (
      <div className='flex justify-between items-start gap-x-4 py-2 border-b border-gray-100'>
        <span className='text-gray-600 text-sm flex-shrink-0'>{label}</span>
        <span className='text-gray-900 text-sm font-medium text-right'>{value}</span>
      </div>
    );
  };

  const handleScrollChatBottom = (): void => {
    window.setTimeout(() => {
      const chatMessageBox = document.getElementById('chat-message-box');
      if (chatMessageBox) {
        chatMessageBox.scrollTo({ top: chatMessageBox.scrollHeight, behavior: 'smooth' });
      }
    }, 200);
  };

  const handleSendMessage = async (): Promise<void> => {
    if (!inputValue.trim() || isLoading) return;

    const userMessageText = inputValue.trim();
    const userMessage: AiChatMessage = {
      id: Date.now(),
      role: 'user',
      content: userMessageText
    };

    chat.addMessage(userMessage);
    setInputValue('');
    setIsLoading(true);
    setShowAbortCta(false);
    handleScrollChatBottom();

    try {
      const history = messages.filter(msg => msg.role !== 'internal');

      const aiNuxRequest: AiNuxRequest = {
        message: userMessageText,
        history,
        nuxData: buildAiFamilyNuxFromStore(familyRegistration),
        currentDate: todayYmd(),
        timezone: getUserTimeZone()
      };
      const response = await AiService.generateNux(aiNuxRequest);

      if (response.data?.data) {
        const { message, nuxData, nuxActions } = response.data.data;

        if (nuxData) {
          syncStoreFromAiNux(familyRegistration, nuxData);
        }

        const assistantMessage: AiChatMessage = {
          id: Date.now(),
          role: 'assistant',
          content: message
        };

        chat.addMessage(assistantMessage);

        if (nuxActions?.readyForSummary && hasEnoughInfo()) {
          addSummaryCard();
          return;
        }
        if (nuxActions?.abort) {
          setShowAbortCta(true);
        } else {
          setShowAbortCta(false);
        }
      } else {
        console.warn('AI response data was empty.');
        setShowAbortCta(true);
      }
    } catch (error) {
      console.error('Error calling AI service:', error);
      const errorMessage: AiChatMessage = {
        id: Date.now(),
        role: 'internal',
        content: t('pages.nux.family.assistant.error'),
      };
      chat.addMessage(errorMessage);
      setShowAbortCta(true);
    } finally {
      setIsLoading(false);
      handleScrollChatBottom();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      handleSendMessage();
    }
  };

  const hasLocation = (): boolean => {
    const { address } = familyRegistration.profile || {};
    return !!(address?.city && address?.zip);
  };

  const hasLookingFor = (): boolean => !!familyRegistration.lookingForId;

  const hasScheduleType = (): boolean => !!familyRegistration.availabilityType;

  const hasEnoughInfo = (): boolean => {
    return hasLocation() && hasLookingFor() && hasScheduleType();
  };

  const addSummaryCard = (): void => {
    const summaryCard: AiChatMessage = {
      id: Date.now(),
      role: 'internal',
      content: 'summaryCard',
    };
    chat.addMessage(summaryCard);
  };

  const renderSummaryCard = (messageId: number): JSX.Element => {
    return (
      <div key={`summary-card-${messageId}`} className='mx-auto lg:w-1/2 bg-white rounded-lg shadow-sm border border-gray-200 p-6'>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-semibold text-gray-900'>
            {t('pages.nux.family.assistant.summaryTitle')}
          </h3>
        </div>
        <div className='space-y-1'>
          {renderSummaryItem(t('pages.nux.family.assistant.careType'), getCareTypeLabel())}
          {renderSummaryItem(t('pages.nux.family.assistant.location'), getLocationString())}
          {familyRegistration.lookingForId === bookingVerticalType.CHILD_CARE &&
            renderSummaryItem(t('pages.nux.family.assistant.childrenAges'), getChildrenAgesString())}
          {familyRegistration.lookingForId === bookingVerticalType.PET_CARE &&
            renderSummaryItem(t('pages.nux.family.assistant.pets'), getPetsString())}
          {renderSummaryItem(t('pages.nux.family.assistant.scheduleType'), familyRegistration.availabilityType)}
          {renderSummaryItem(t('pages.nux.family.assistant.days'), familyRegistration.daysOfTheWeekString)}
          {renderSummaryItem(t('pages.nux.family.assistant.dates'), getDatesString())}
          {renderSummaryItem(t('pages.nux.family.assistant.careTimes'), getCareTimesString())}
          {renderSummaryItem(t('pages.nux.family.assistant.languages'), getLabelsString(familyRegistration.languages))}
          {renderSummaryItem(t('pages.nux.family.assistant.specialSituations'), getLabelsString(familyRegistration.specialSituations))}
          {renderSummaryItem(t('pages.nux.family.assistant.helpNeeded'), getLabelsString(familyRegistration.specialServices))}
          {renderSummaryItem(t('pages.nux.family.assistant.teachingSkills'), getLabelsString(familyRegistration.teachingSkills))}
          {renderSummaryItem(t('pages.nux.family.assistant.specialExperiences'), getLabelsString(familyRegistration.specialExperiences))}
          {renderSummaryItem(t('pages.nux.family.assistant.schedule'), familyRegistration.jobType)}
          {renderSummaryItem(t('pages.nux.family.assistant.details'), familyRegistration.details)}
        </div>
        <div className='mt-6 pt-4 border-t border-gray-200'>
          <p className='text-sm font-semibold text-gray-600 mb-4'>
            {t('pages.nux.family.assistant.whatNext')}
          </p>
          <div className='space-y-3'>
            <Button buttonType={ButtonType.primary} onClick={goToSitterSearch} className='w-full'>
              {t('pages.nux.family.assistant.viewCaregivers')}
            </Button>
            {familyRegistration.jobType && familyRegistration.startDate && familyRegistration.lookingForId && (
              <Button buttonType={ButtonType.secondary} onClick={goToJobPost} className='w-full'>
                {t('pages.nux.family.assistant.postJob')}
              </Button>
            )}
            <Button buttonType={ButtonType.secondary} onClick={goToDashboard} className='w-full'>
              {t('pages.nux.family.assistant.goToDashboard')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <NuxContainer
      familyRegistration={familyRegistration}
      navigate={navigate}
      tracker={tracker}
      title={<>{t('pages.nux.family.assistant.title')} <BetaBadge label={t('components.betaBadge.label')} className='align-middle mb-0.5' /></>}
      hideContinue
    >
      <div className='flex flex-col h-[calc(100dvh-200px)] md:h-[60vh] lg:h-[70vh]'>
        <div id='chat-message-box' className='flex-1 overflow-y-auto overscroll-contain p-4 space-y-4'>
          {messages.map((message) => (
            message.role === 'internal' ? (
              message.content === 'summaryCard' ? (
                renderSummaryCard(message.id)
              ) : (
                <div
                  key={message.id}
                  className='max-w-[75%] rounded-2xl px-4 py-2 bg-gray-100 text-gray-800'
                >
                  <p className='text-sm whitespace-pre-wrap'>{message.content}</p>
                </div>
              )
            ) : (
              <div
                key={message.id}
                className={`flex items-start gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    message.role === 'assistant' ? 'bg-sapphire-100' : 'bg-blue-100 text-blue-600'
                  }`}
                >
                  {message.role === 'assistant' ? (
                    <Avatar imageUrl={getUsAvatarSrc()} alt={t('common.urbansitterAvatar')} className='w-5 h-5' size={20} />
                  ) : (
                    <FontAwesomeIcon icon={faUser} className='w-4 h-4' />
                  )}
                </div>
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                    message.role === 'assistant'
                      ? 'bg-gray-100 text-gray-800'
                      : 'bg-sapphire-500 text-white'
                  }`}
                >
                  <p className='text-sm whitespace-pre-wrap'>{message.content}</p>
                </div>
              </div>
            )
          ))}
          {isLoading && (
            <div className='flex items-start gap-3'>
              <div className='flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-sapphire-100'>
                <Avatar imageUrl={getUsAvatarSrc()} alt={t('common.urbansitterAvatar')} className='w-5 h-5' size={20} />
              </div>
              <div className='bg-gray-100 rounded-2xl px-4 py-2'>
                <p className='text-sm text-gray-500'>{t('pages.nux.family.assistant.thinking').replace(/\.+$/, '')}<span>{thinkingDots}</span></p>
              </div>
            </div>
          )}
        </div>
        <div className='border-t border-gray-200 p-4'>
          {inputValue.length > 0 && (
            <div className='text-right text-xs mb-1 mr-[50px]'>
              {inputValue.length < 1000 && <span className='text-gray-500'>{1000 - inputValue.length} {t('pages.nux.family.details.charactersRemaining')}</span>}
              {inputValue.length === 1000 && <span className='text-yellow-500'>{t('pages.nux.family.details.charLimitReached')}</span>}
            </div>
          )}
          <div className='flex items-center gap-2'>
            <SpeechRecognition isLoading={isLoading} setInput={setInputValue} />
            <input
              ref={inputRef}
              type='text'
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={1000}
              placeholder={t('pages.nux.family.assistant.placeholder')}
              disabled={isLoading}
              className='flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-sapphire-500 focus:border-transparent disabled:bg-gray-100'
              aria-label={t('pages.nux.family.assistant.placeholder')}
            />
            <Button
              buttonType={ButtonType.primary}
              buttonSize={ButtonSize.small}
              onClick={handleSendMessage}
              disabled={isLoading || !inputValue.trim()}
              className='w-10 h-10 !py-0 flex items-center justify-center'
              title={t('pages.nux.family.assistant.send')}
              aria-label={t('pages.nux.family.assistant.send')}
            >
              <FontAwesomeIcon icon={faPaperPlane} className='w-4 h-4' />
            </Button>
          </div>
          {showAbortCta && (
          <div className='pt-6 mx-4'>
            <Button
              buttonType={ButtonType.secondary}
              onClick={(): void => navigate(Routes.parent.registration.location)}
              className='w-full'
            >
              {t('pages.nux.family.assistant.abort')}
            </Button>
          </div>
          )}
        </div>
      </div>
    </NuxContainer>
  );
});
